import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import {
  FiEdit2,
  FiRefreshCw,
  FiSave,
  FiTarget,
  FiTrash2,
  FiTrendingUp
} from 'react-icons/fi';
import { API_ENDPOINTS } from '../../utils/api';
import { fetchAllInvoices } from '../../utils/zovcurInvoiceApi';
import { formatINR } from '../../utils/currency';
import {
  MONTH_NAMES,
  QUARTER_LABELS,
  aggregateInvoicesForSalesman,
  calcReachPercent,
  formatPeriodLabel,
  getReachTone
} from '../../utils/salesPerformanceUtils';

const emptyForm = () => ({
  salesmanId: '',
  periodType: 'monthly',
  year: new Date().getFullYear(),
  month: new Date().getMonth() + 1,
  quarter: Math.floor(new Date().getMonth() / 3) + 1,
  amountTarget: ''
});

const SalesTargetReports = () => {
  const [salesmen, setSalesmen] = useState([]);
  const [targets, setTargets] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm());

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [salesmenRes, targetsRes, invoiceData] = await Promise.all([
        axios.get(API_ENDPOINTS.getSalesmen, { headers: authHeaders() }),
        axios.get(API_ENDPOINTS.getSalesTargets, { headers: authHeaders() }),
        fetchAllInvoices()
      ]);
      setSalesmen(salesmenRes.data || []);
      setTargets(targetsRes.data || []);
      setInvoices(invoiceData || []);
    } catch (error) {
      console.error('Failed to load sales target data:', error);
      Swal.fire('Error', 'Failed to load sales target data', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const selectedSalesman = useMemo(
    () => salesmen.find((s) => String(s._id) === String(form.salesmanId)),
    [salesmen, form.salesmanId]
  );

  const currentAchieved = useMemo(() => {
    if (!selectedSalesman) {
      return { amount: 0, invoices: 0 };
    }
    const stats = aggregateInvoicesForSalesman(
      invoices,
      selectedSalesman.name,
      form.periodType,
      form.year,
      form.month,
      form.quarter
    );
    return { amount: stats.amount, invoices: stats.invoices };
  }, [selectedSalesman, invoices, form]);

  const handleFormChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const fillFormFromTarget = (target) => {
    setForm({
      salesmanId: target.salesmanId,
      periodType: target.periodType,
      year: target.year,
      month: target.month || new Date().getMonth() + 1,
      quarter: target.quarter || Math.floor(new Date().getMonth() / 3) + 1,
      amountTarget: String(target.amountTarget || '')
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.salesmanId) {
      Swal.fire('Required', 'Please select a salesman', 'warning');
      return;
    }
    if (!Number(form.amountTarget) || Number(form.amountTarget) <= 0) {
      Swal.fire('Required', 'Please enter a price target amount', 'warning');
      return;
    }

    const salesman = salesmen.find((s) => String(s._id) === String(form.salesmanId));
    if (!salesman) return;

    setSaving(true);
    try {
      const payload = {
        salesmanId: salesman._id,
        salesmanName: salesman.name,
        periodType: form.periodType,
        year: Number(form.year),
        stripTarget: 0,
        amountTarget: Number(form.amountTarget) || 0
      };

      if (form.periodType === 'monthly') payload.month = Number(form.month);
      if (form.periodType === 'quarterly') payload.quarter = Number(form.quarter);

      await axios.post(API_ENDPOINTS.saveSalesTarget, payload, { headers: authHeaders() });
      Swal.fire('Saved', 'Sales target saved successfully', 'success');
      setForm(emptyForm());
      loadData();
    } catch (error) {
      Swal.fire('Error', error.response?.data?.error || 'Failed to save target', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (target) => {
    const result = await Swal.fire({
      title: 'Delete target?',
      text: `${target.salesmanName} — ${formatPeriodLabel(
        target.periodType,
        target.year,
        target.month,
        target.quarter
      )}`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete'
    });
    if (!result.isConfirmed) return;

    try {
      await axios.delete(API_ENDPOINTS.deleteSalesTarget(target._id), { headers: authHeaders() });
      loadData();
    } catch (error) {
      Swal.fire('Error', 'Failed to delete target', 'error');
    }
  };

  const sortedTargets = useMemo(
    () =>
      [...targets].sort((a, b) => {
        if (b.year !== a.year) return b.year - a.year;
        return String(b.periodKey).localeCompare(String(a.periodKey));
      }),
    [targets]
  );

  const amountReach = calcReachPercent(currentAchieved.amount, Number(form.amountTarget) || 0);

  return (
    <div className="p-4 md:p-6 bg-gradient-to-br from-slate-100 via-teal-50/30 to-slate-100 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-[#1a365d] flex items-center gap-3">
              <FiTarget className="text-teal-600" />
              Sales Target & Reports
            </h1>
            <p className="text-slate-600 mt-1">
              Select a salesman and set price targets by month, quarter, or year
            </p>
          </div>
          <button
            type="button"
            onClick={loadData}
            className="px-4 py-2 rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 flex items-center gap-2"
          >
            <FiRefreshCw /> Refresh
          </button>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <div className="xl:col-span-1 bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
            <h2 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
              <FiEdit2 className="text-teal-600" />
              Set Sales Target
            </h2>
            <form onSubmit={handleSave} className="space-y-4">
              <label className="block text-sm">
                <span className="text-slate-600 mb-1 block">Salesman *</span>
                <select
                  value={form.salesmanId}
                  onChange={(e) => handleFormChange('salesmanId', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  required
                >
                  <option value="">Select salesman</option>
                  {salesmen.map((salesman) => (
                    <option key={salesman._id} value={salesman._id}>
                      {salesman.name} ({salesman.empNo})
                    </option>
                  ))}
                </select>
              </label>

              <label className="block text-sm">
                <span className="text-slate-600 mb-1 block">Duration</span>
                <select
                  value={form.periodType}
                  onChange={(e) => handleFormChange('periodType', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                >
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </label>

              <label className="block text-sm">
                <span className="text-slate-600 mb-1 block">Year</span>
                <input
                  type="number"
                  value={form.year}
                  onChange={(e) => handleFormChange('year', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  min="2020"
                  max="2100"
                  required
                />
              </label>

              {form.periodType === 'monthly' ? (
                <label className="block text-sm">
                  <span className="text-slate-600 mb-1 block">Month</span>
                  <select
                    value={form.month}
                    onChange={(e) => handleFormChange('month', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  >
                    {MONTH_NAMES.map((name, index) => (
                      <option key={name} value={index + 1}>
                        {name}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}

              {form.periodType === 'quarterly' ? (
                <label className="block text-sm">
                  <span className="text-slate-600 mb-1 block">Quarter</span>
                  <select
                    value={form.quarter}
                    onChange={(e) => handleFormChange('quarter', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  >
                    {[1, 2, 3, 4].map((q) => (
                      <option key={q} value={q}>
                        Q{q} ({QUARTER_LABELS[q]})
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}

              <label className="block text-sm">
                <span className="text-slate-600 mb-1 block flex items-center gap-1">
                  <FiTrendingUp /> Price Target (₹)
                </span>
                <input
                  type="number"
                  value={form.amountTarget}
                  onChange={(e) => handleFormChange('amountTarget', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  min="0"
                  placeholder="e.g. 500000"
                  required
                />
              </label>

              {selectedSalesman ? (
                <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-sm space-y-2">
                  <p className="font-semibold text-slate-700">
                    Current achieved — {formatPeriodLabel(form.periodType, form.year, form.month, form.quarter)}
                  </p>
                  <p>
                    Sales amount: <strong>{formatINR(currentAchieved.amount)}</strong>
                    {form.amountTarget ? (
                      <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${getReachTone(amountReach)}`}>
                        {amountReach}%
                      </span>
                    ) : null}
                  </p>
                  <p className="text-xs text-slate-500">
                    {currentAchieved.invoices} invoice{currentAchieved.invoices !== 1 ? 's' : ''} in this period
                  </p>
                </div>
              ) : null}

              <button
                type="submit"
                disabled={saving}
                className="w-full px-4 py-2.5 rounded-lg bg-[#1a365d] text-white hover:bg-[#153050] flex items-center justify-center gap-2 disabled:opacity-60"
              >
                <FiSave /> {saving ? 'Saving…' : 'Save Target'}
              </button>
            </form>
          </div>

          <div className="xl:col-span-2 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-200">
              <h2 className="font-bold text-slate-800">Saved Targets</h2>
              <p className="text-xs text-slate-500 mt-1">
                Price targets set here appear on User Performance with reach %
              </p>
            </div>
            {loading ? (
              <div className="p-8 text-center text-slate-500">Loading targets…</div>
            ) : sortedTargets.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                No targets yet. Select a salesman and set a price target.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b">
                      <th className="px-4 py-3 text-left">Salesman</th>
                      <th className="px-4 py-3 text-left">Period</th>
                      <th className="px-4 py-3 text-right">Price Target</th>
                      <th className="px-4 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedTargets.map((target) => (
                      <tr key={target._id} className="border-b hover:bg-slate-50">
                        <td className="px-4 py-3 font-medium">{target.salesmanName}</td>
                        <td className="px-4 py-3">
                          <span className="capitalize">{target.periodType}</span>
                          <span className="text-slate-500 ml-1">
                            · {formatPeriodLabel(
                              target.periodType,
                              target.year,
                              target.month,
                              target.quarter
                            )}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          {formatINR(target.amountTarget || 0)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => fillFormFromTarget(target)}
                              className="p-2 rounded-lg text-teal-700 hover:bg-teal-50"
                              title="Edit"
                            >
                              <FiEdit2 />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(target)}
                              className="p-2 rounded-lg text-rose-600 hover:bg-rose-50"
                              title="Delete"
                            >
                              <FiTrash2 />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesTargetReports;
